import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, BadgeCheck, Loader2, Camera, Film, X, Clock, Calendar, Trash2 } from "lucide-react";
import RichTextEditor from "@/components/common/RichTextEditor";
import ImageCropDialog from "@/components/home/ImageCropDialog";
import { InfoCard } from "@/components/toggles";
import RichTextView from "@/components/common/RichTextView";
import AIAssistBar from "@/components/ai/AIAssistBar";

const ADMIN_ROLES = new Set(["ADMIN", "OWNER", "SUPPORT"]);
// iter160 · Max Video-Länge Platform-Post = 1200s (identisch zu Creator-Composer)
const MAX_PLATFORM_VIDEO_SEC = 1200;

export default function AdminPlatformPostPage() {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState("IMAGE"); // IMAGE | VIDEO (iter160)
  // iter165 · Crop-Dialog
  const [cropFile, setCropFile] = useState(null);
  const [daysOnline, setDaysOnline] = useState(3);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [postList, setPostList] = useState([]);
  const [listBusy, setListBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef(null);
  const videoRef = useRef(null);

  // Defense-in-depth: Non-Admin darf die Seite nicht sehen (Backend blockt separat via 403)
  useEffect(() => {
    if (!ready) return;
    if (!user || user === false) {
      navigate("/login", { replace: true });
      return;
    }
    if (!ADMIN_ROLES.has((user.role || "").toUpperCase())) {
      navigate("/", { replace: true });
    }
  }, [ready, user, navigate]);

  const loadList = useCallback(async () => {
    setListBusy(true);
    try {
      const { data } = await api.get("/platform/posts");
      setPostList(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[platform-post] loadList failed", err);
      setPostList([]);
    } finally {
      setListBusy(false);
    }
  }, []);

  // iter159 · Lade Liste beim Mount für Admin/Owner/Support unabhängig vom user-Redirect
  useEffect(() => {
    if (!ready) return;
    if (!user || user === false) return;
    const role = (user.role || "").toUpperCase();
    if (!ADMIN_ROLES.has(role)) return;
    loadList();
  }, [ready, user, loadList]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    // iter165 · Titel entfernt — Platform-Posts zeigen im Feed keinen Titel mehr.
    //           Pflicht ist eine Beschreibung (analog Creator-Composer).
    const plainDesc = (description || "").replace(/<[^>]+>/g, "").trim();
    if (!plainDesc && !mediaUrl) {
      setError("Beschreibung oder Medium erforderlich.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: "",
        description: description || "",
        media_url: mediaUrl || undefined,
        media_type: mediaUrl ? mediaType : "IMAGE",
        days_online: daysOnline,
      };
      if (scheduleEnabled && scheduledAt) {
        // datetime-local ist lokaler Wert ohne TZ → in ISO umwandeln
        const local = new Date(scheduledAt);
        if (!isNaN(local.getTime())) payload.scheduled_at = local.toISOString();
      }
      await api.post("/platform/posts", payload);
      setSuccess(true);
      setTitle("");
      setDescription("");
      setMediaUrl("");
      setMediaType("IMAGE");
      setDaysOnline(3);
      setScheduleEnabled(false);
      setScheduledAt("");
      window.scrollTo({ top: 0, behavior: "smooth" });
      loadList();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : d?.message || "Fehler beim Publishen.");
    } finally {
      setBusy(false);
    }
  };

  const onFileSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    // iter165 · Bild → Crop-Dialog vor Upload
    setError("");
    setCropFile(f);
  };

  const uploadCroppedImage = async (blob) => {
    setUploadBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", blob, blob.name || "platform-post.jpg");
      const { data } = await api.post("/upload?area_scope=SOCIAL", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMediaUrl(`/api/files/${data.path}`);
      setMediaType("IMAGE");
    } catch (err) {
      setError(formatError(err) || "Upload fehlgeschlagen");
    } finally {
      setUploadBusy(false);
    }
  };

  // iter160 · Video-Upload (max 1200s Länge), analog zum Creator-Composer
  const probeVideoDuration = (file) => new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { const d = v.duration; window.URL.revokeObjectURL(v.src); resolve(d); };
    v.onerror = () => resolve(null);
    v.src = URL.createObjectURL(file);
  });
  const onVideoSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    setUploadBusy(true);
    setError("");
    try {
      const dur = await probeVideoDuration(f);
      if (dur == null) { setError("Video konnte nicht gelesen werden."); return; }
      if (dur > MAX_PLATFORM_VIDEO_SEC + 0.5) {
        setError(`Video zu lang (${Math.round(dur)}s). Max ${MAX_PLATFORM_VIDEO_SEC}s (20 Min).`);
        return;
      }
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/video?area_scope=SOCIAL", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMediaUrl(`/api/files/${data.path}`);
      setMediaType("VIDEO");
    } catch (err) {
      setError(formatError(err) || "Video-Upload fehlgeschlagen");
    } finally {
      setUploadBusy(false);
    }
  };

  const removePost = async (postId) => {
    if (!window.confirm("Diesen Platform-Beitrag wirklich löschen?")) return;
    setDeletingId(postId);
    try {
      await api.delete(`/platform/posts/${postId}`);
      setPostList((p) => p.filter((x) => x.post_id !== postId));
    } catch (err) {
      setError(formatError(err) || "Löschen fehlgeschlagen");
    } finally {
      setDeletingId(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-platform-post-page" className="max-w-2xl mx-auto pb-16">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="platform-post-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-base font-bold text-[#0A0A0A] flex items-center gap-2">
          <BadgeCheck size={18} /> Offizieller Platform-Beitrag
        </h1>
      </div>

      <div className="px-4 pt-4 pb-3">
        <InfoCard variant="info" size="sm">
          <p className="text-xs">
            Dieser Beitrag erscheint im öffentlichen Feed unter dem Autor <b>stilltouched</b> mit einem
            &bdquo;Offiziell&ldquo;-Badge. Nutze <b>@handle</b> im Text, um Creator zu pushen — sie werden automatisch verlinkt.
          </p>
        </InfoCard>
      </div>

      {success && (
        <div className="px-4 pb-3">
          <InfoCard variant="success" size="sm">Beitrag veröffentlicht.</InfoCard>
        </div>
      )}
      {error && (
        <div className="px-4 pb-3">
          <InfoCard variant="danger" size="sm">{error}</InfoCard>
        </div>
      )}

      <form onSubmit={submit} className="px-4 space-y-4">
        {/* iter165 · Titel entfernt — im Feed nicht mehr sichtbar. */}
        <div>
          <label className="text-[10px] font-bold text-[#999] uppercase tracking-wider block mb-1">Beschreibung *</label>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="Text, Ankündigung, Creator-Push mit @handle…"
            minHeight="160px"
            testId="platform-post-body"
          />
          {/* iter160 · AI-Assist analog Creator-Composer, keine Monetization-Optionen */}
          <AIAssistBar area="SOCIAL" value={description} onApply={setDescription} fieldType="caption" testIdSuffix="platform-post" />
        </div>
        <div>
          <label className="text-[10px] font-bold text-[#999] uppercase tracking-wider block mb-1">Bild oder Video (optional)</label>
          <div className="flex items-start gap-3 flex-wrap">
            {mediaUrl ? (
              <div className="relative w-full max-w-sm">
                {mediaType === "VIDEO" ? (
                  <video
                    src={mediaUrl}
                    controls
                    playsInline
                    data-testid="platform-post-video-preview"
                    className="w-full max-h-64 rounded-lg bg-black border border-[#E5E5E5]"
                  />
                ) : (
                  <img src={mediaUrl} alt="Vorschau" data-testid="platform-post-image-preview" className="w-full max-h-48 object-contain border border-[#E5E5E5] rounded-lg bg-[#F5F5F5]" />
                )}
                <button
                  type="button"
                  onClick={() => { setMediaUrl(""); setMediaType("IMAGE"); if (fileRef.current) fileRef.current.value = ""; if (videoRef.current) videoRef.current.value = ""; }}
                  data-testid="platform-post-image-remove"
                  className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-[#E5E5E5] shadow flex items-center justify-center text-red-500 hover:bg-red-50"
                  title="Medium entfernen"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadBusy}
                  data-testid="platform-post-image-upload"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50 transition-colors"
                >
                  {uploadBusy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
                  Bild
                </button>
                <button
                  type="button"
                  onClick={() => videoRef.current?.click()}
                  disabled={uploadBusy}
                  data-testid="platform-post-video-upload"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50 transition-colors"
                >
                  {uploadBusy ? <Loader2 size={12} className="animate-spin" /> : <Film size={12} />}
                  Video
                </button>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFileSelected}
              data-testid="platform-post-image-file-input"
            />
            <input
              ref={videoRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={onVideoSelected}
              data-testid="platform-post-video-file-input"
            />
          </div>
          <p className="text-[10px] text-[#999] mt-1">
            Bild oder Video · max. {MAX_PLATFORM_VIDEO_SEC}s (20 Min) pro Video · Kein Trinkgeld/Monetization bei Platform-Posts.
          </p>
        </div>
        {/* iter157 · Online-Dauer + optional zeitgesteuerter Publish */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-[#E5E5E5] pt-4">
          <div>
            <label className="text-[10px] font-bold text-[#999] uppercase tracking-wider flex items-center gap-1 mb-2">
              <Clock size={11} /> Online-Dauer · {daysOnline} {daysOnline === 1 ? "Tag" : "Tage"}
            </label>
            <input
              type="range"
              min={1}
              max={30}
              step={1}
              value={daysOnline}
              onChange={(e) => setDaysOnline(parseInt(e.target.value, 10))}
              data-testid="platform-post-days-online"
              className="w-full accent-[#0A0A0A]"
            />
            <div className="flex justify-between text-[10px] text-[#999] mt-1">
              <span>1&nbsp;Tag</span><span>3</span><span>15</span><span>30&nbsp;Tage</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[#999] uppercase tracking-wider flex items-center gap-1 mb-2">
              <Calendar size={11} /> Zeitgesteuert publishen (optional)
            </label>
            <label className="flex items-center gap-2 text-xs text-[#0A0A0A] mb-2">
              <input
                type="checkbox"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
                data-testid="platform-post-schedule-toggle"
                className="accent-[#0A0A0A]"
              />
              Zu einem späteren Zeitpunkt sichtbar machen
            </label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              disabled={!scheduleEnabled}
              data-testid="platform-post-schedule-datetime"
              className="w-full h-10 px-3 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] disabled:bg-[#F5F5F5] disabled:text-[#999]"
            />
          </div>
        </div>
        <div className="pt-2">
          <button
            type="submit"
            data-testid="platform-post-submit"
            disabled={busy}
            className="w-full rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#222] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <BadgeCheck size={14} />}
            Als &bdquo;stilltouched&ldquo; veröffentlichen
          </button>
        </div>
      </form>

      {/* iter154 · Live-Vorschau — zeigt wie der Beitrag im Feed erscheinen wird */}
      {(title || description || mediaUrl) && (
        <div className="mt-8 px-4">
          <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-2">Vorschau</p>
          <div
            data-testid="platform-post-preview"
            className="rounded-2xl border border-[#E5E5E5] bg-white overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 py-3">
              <div className="w-9 h-9 rounded-full bg-[#F0F0F0] flex items-center justify-center text-xs font-bold text-[#0A0A0A]">
                s
              </div>
              <p className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-1.5">
                stilltouched
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#0A0A0A] text-white uppercase tracking-wider"
                >
                  <BadgeCheck size={10} /> Offiziell
                </span>
              </p>
            </div>
            {title && <p className="px-4 pb-2 text-base font-semibold text-[#0A0A0A]">{title}</p>}
            {description && (
              <RichTextView
                html={description}
                className="px-4 pb-2 text-sm leading-relaxed text-[#0A0A0A] prose prose-sm max-w-none"
              />
            )}
            {mediaUrl && (
              <div className="w-full bg-[#F5F5F5]">
                {mediaType === "VIDEO" ? (
                  <video src={mediaUrl} controls playsInline className="w-full max-h-96 bg-black" />
                ) : (
                  <img src={mediaUrl} alt="" className="w-full max-h-96 object-contain" />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* iter157 · Übersicht bereits erstellter Platform-Posts */}
      <div className="mt-10 px-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider">
            Bereits veröffentlicht ({postList.length})
          </p>
          <button
            type="button"
            onClick={loadList}
            data-testid="platform-post-refresh"
            className="text-[10px] text-[#666] hover:text-[#0A0A0A] underline"
          >
            {listBusy ? "Lädt…" : "Aktualisieren"}
          </button>
        </div>
        {postList.length === 0 && !listBusy && (
          <p className="text-xs text-[#999] py-3">Noch keine Platform-Beiträge veröffentlicht.</p>
        )}
        <ul data-testid="platform-post-list" className="space-y-2">
          {postList.map((p) => {
            const isDeleted = (p.status || "").startsWith("DELETED");
            const badge = isDeleted
              ? { label: "gelöscht", cls: "bg-[#F5F5F5] text-[#666]" }
              : p.is_live
              ? { label: "live", cls: "bg-green-100 text-green-700" }
              : (p.published_at || "") > new Date().toISOString()
              ? { label: "geplant", cls: "bg-blue-100 text-blue-700" }
              : { label: "abgelaufen", cls: "bg-orange-100 text-orange-700" };
            return (
              <li
                key={p.post_id}
                data-testid={`platform-post-list-item-${p.post_id}`}
                className={`flex items-center gap-3 p-3 border border-[#E5E5E5] rounded-lg bg-white ${isDeleted ? "opacity-50" : ""}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider ${badge.cls}`}>
                      {badge.label}
                    </span>
                    <p className="text-sm font-semibold text-[#0A0A0A] truncate">{p.title || "(ohne Titel)"}</p>
                  </div>
                  <p className="text-[10px] text-[#999]">
                    Publish: {fmtDate(p.published_at)} · Läuft ab: {fmtDate(p.expires_at)} ({p.days_online || 3}&nbsp;Tage)
                  </p>
                </div>
                {!isDeleted && (
                  <button
                    type="button"
                    onClick={() => removePost(p.post_id)}
                    disabled={deletingId === p.post_id}
                    data-testid={`platform-post-delete-${p.post_id}`}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-red-300 text-red-600 text-[11px] hover:bg-red-50 disabled:opacity-50"
                  >
                    {deletingId === p.post_id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                    Löschen
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {/* iter165 · Bild-Ausschnitt vor Upload (wie Profilbild-Upload) */}
      {cropFile && (
        <ImageCropDialog
          file={cropFile}
          aspect={16/9}
          title="Ausschnitt für Platform-Post-Bild wählen"
          onCancel={() => setCropFile(null)}
          onCropped={async (blob) => { setCropFile(null); await uploadCroppedImage(blob); }}
        />
      )}
    </div>
  );
}
